import { Prognose, PrognoseStatus } from "@/entities/dossier-details";
import { PrognoseActieDto } from "@/shared/types";
import { hourFormatInAmsterdam } from "@/shared/utils";
import { parseISO } from "date-fns";
import { ActieDtoTransformatieError } from "./actie-error";
import { BaseActie } from "./base-actie";

export class PrognoseActieTransformatieError extends ActieDtoTransformatieError {
    private constructor(wijzigingType: BaseActie["wijzigingType"], reason: string) {
        super("Prognose", wijzigingType, reason);
    }

    public static geenNieuweWaardeVoorGewijzigd(): PrognoseActieTransformatieError {
        return new PrognoseActieTransformatieError("Gewijzigd", `nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }

    public static geenNieuweWaardeVoorToegevoegd(): PrognoseActieTransformatieError {
        return new PrognoseActieTransformatieError("Toegevoegd", `nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }
}

function parseAndFormatHoursAndMinutes(date: string): string {
    const parsedDate = parseISO(date);
    return hourFormatInAmsterdam(parsedDate);
}

export type PrognoseToegevoegdActie = BaseActie & {
    type: "Prognose";
    wijzigingType: "Toegevoegd";
    nieuweWaarde: Prognose;
};

// Not in use right now, but for example
export const PrognoseToegevoegdActie = {
    fromDto(dto: PrognoseActieDto): PrognoseActie {
        if (!dto.nieuweWaarde) {
            throw PrognoseActieTransformatieError.geenNieuweWaardeVoorToegevoegd();
        }
        const nieuwePrognoseTijd = parseAndFormatHoursAndMinutes(dto.nieuweWaarde.datumTijd);

        return {
            id: dto.id,
            type: "Prognose",
            timelineContent: {
                titel: "Prognose toevoegen",
                beschrijving: "Prognose is toegevoegd in Spoorweb",
                actieTekst: "Controleer en bevestig prognose",
            },
            dialogContent: {
                titel: `Toevoegen prognose: ${nieuwePrognoseTijd}`,
                beschrijving: `Spoorweb heeft de prognose gezet op ${nieuwePrognoseTijd}. Wil je deze update doorvoeren in het SEIN dossier?`,
                weigerenTitel: "Prognose weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je de prognose wilt weigeren?",
                weigerenActieTekst: "Prognose weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: "Toegevoegd",
            nieuweWaarde: Prognose.fromDto(dto.nieuweWaarde),
        };
    },
};

export type PrognoseGewijzigdActie = BaseActie & {
    type: "Prognose";
    wijzigingType: "Gewijzigd";
    nieuweWaarde: Prognose;
};

export const PrognoseGewijzigdActie = {
    fromDto(dto: PrognoseActieDto): PrognoseGewijzigdActie {
        if (!dto.nieuweWaarde) {
            throw PrognoseActieTransformatieError.geenNieuweWaardeVoorGewijzigd();
        }
        const nieuwePrognoseTijd = parseAndFormatHoursAndMinutes(dto.nieuweWaarde.datumTijd);

        return {
            id: dto.id,
            type: "Prognose",
            timelineContent: {
                titel: "Prognose vernieuwen",
                beschrijving: "Prognose is geüpdatet in Spoorweb",
                actieTekst: "Controleer en bevestig prognose",
            },
            dialogContent: {
                titel: `Update prognose: ${nieuwePrognoseTijd}`,
                beschrijving: `Spoorweb heeft de prognose geüpdatet naar ${nieuwePrognoseTijd}. Wil je deze update doorvoeren in het SEIN dossier?`,
                weigerenTitel: "Prognose weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je de prognose wilt weigeren?",
                weigerenActieTekst: "Prognose weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: "Gewijzigd",
            nieuweWaarde: Prognose.fromDto(dto.nieuweWaarde),
        };
    },
};

export type PrognoseActie = PrognoseToegevoegdActie | PrognoseGewijzigdActie;

export const PrognoseActie = {
    fromDto: (dto: PrognoseActieDto): PrognoseActie => {
        switch (dto.wijzigingType) {
            case "Toegevoegd":
                return PrognoseToegevoegdActie.fromDto(dto);
            case "Gewijzigd":
                return PrognoseGewijzigdActie.fromDto(dto);
            default:
                throw ActieDtoTransformatieError.nietOndersteundWijzigingTypeVoorActie(dto.type, dto.wijzigingType);
        }
    },
    withStatusAndToelichting: (actie: PrognoseActie, status: PrognoseStatus, toelichting: string): PrognoseActie => {
        return {
            ...actie,
            nieuweWaarde: {
                ...actie.nieuweWaarde,
                status,
            },
            toelichting,
        };
    },
    toDto: (actie: PrognoseActie): PrognoseActieDto => {
        return {
            id: actie.id,
            type: actie.type,
            verstuurdOpDatumTijd: actie.verstuurdOpDatumTijd.toISOString(),
            status: actie.status,
            toelichting: actie.toelichting,
            nieuweWaarde: Prognose.toDto(actie.nieuweWaarde) as PrognoseActieDto["nieuweWaarde"],
            wijzigingType: actie.wijzigingType,
        };
    },
};
